import { useState, useRef, useEffect } from 'react'
import {
  Input,
  Box,
  Text,
  HStack,
  IconButton,
  Spinner,
  InputGroup,
  Portal,
  type InputProps,
} from '@chakra-ui/react';
import { FiX } from 'react-icons/fi'
import { useQuery } from '@tanstack/react-query'
import { contactSearchApi } from '../../lib/api'

interface ContactSearchSelectProps {
  value?: string
  displayName?: string
  onSelect: (contactId: string | null, name: string | null) => void
  placeholder?: string
  size?: InputProps['size']
  allowFreeText?: boolean
}

export function ContactSearchSelect({
  value,
  displayName,
  onSelect,
  placeholder = 'Search contacts...',
  size = 'sm',
  allowFreeText = false,
}: ContactSearchSelectProps) {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isEditingSelected, setIsEditingSelected] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 })

  // Reset editing state when value changes externally
  useEffect(() => {
    if (value) setIsEditingSelected(false)
  }, [value])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      // Check both the input container and the Portal dropdown
      if (containerRef.current && !containerRef.current.contains(target)
        && (!dropdownRef.current || !dropdownRef.current.contains(target))) {
        // If allowFreeText and there's typed text, save it as free text
        if (allowFreeText && search.trim()) {
          onSelect(null, search.trim())
        }
        setIsOpen(false)
        setIsEditingSelected(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [allowFreeText, search, onSelect])

  // Update dropdown position when open
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      })
    }
  }, [isOpen, debouncedSearch])

  const { data: results, isLoading } = useQuery({
    queryKey: ['contact-search-select', debouncedSearch],
    queryFn: () =>
      contactSearchApi.search({ lastName: debouncedSearch, useSmartNameSearch: true, page: 1, pageSize: 10 }).then(r => r.data),
    enabled: debouncedSearch.length >= 2,
  })

  const handleSelect = (contactId: string, name: string) => {
    onSelect(contactId, name)
    setSearch('')
    setIsOpen(false)
    setIsEditingSelected(false)
  }

  const handleClear = () => {
    onSelect(null, null)
    setSearch('')
    setIsEditingSelected(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && allowFreeText && search.trim()) {
      e.preventDefault()
      onSelect(null, search.trim())
      setSearch('')
      setIsOpen(false)
    }
  }

  // If we have a selected value and aren't searching/editing, show the selected name
  if (value && !isOpen && !isEditingSelected) {
    return (
      <HStack gap={1}>
        <Text
          fontSize={size}
          fontWeight="medium"
          lineClamp={1}
          cursor="pointer"
          _hover={{ color: 'accent' }}
          onClick={() => setIsEditingSelected(true)}
          title="Click to change"
        >
          {displayName || value}
        </Text>
        <IconButton
          aria-label="Clear selection"
          size="xs"
          variant="ghost"
          color="gray.400"
          onClick={handleClear}><FiX /></IconButton>
      </HStack>
    );
  }

  return (
    <Box ref={containerRef} position="relative">
      <InputGroup endElement={isLoading && debouncedSearch.length >= 2 && (
        <Spinner size="xs" />
      )}>
        <Input
          size={size}
          ref={inputRef}
          placeholder={placeholder}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      </InputGroup>

      {isOpen && debouncedSearch.length >= 2 && results?.items && (
        <Portal>
          <Box
            ref={dropdownRef}
            position="absolute"
            top={`${dropdownPos.top}px`}
            left={`${dropdownPos.left}px`}
            width={`${dropdownPos.width}px`}
            bg="surface"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            shadow="lg"
            zIndex={1400}
            maxH="200px"
            overflowY="auto"
          >
            {results.items.length === 0 ? (
              allowFreeText ? (
                <Box
                  px={3} py={2} cursor="pointer"
                  _hover={{ bg: 'accent.soft' }}
                  onClick={() => { onSelect(null, search.trim()); setSearch(''); setIsOpen(false) }}
                >
                  <Text fontSize="sm" color="gray.600">Use "{search.trim()}" as name</Text>
                </Box>
              ) : (
                <Text p={2} fontSize="sm" color="gray.500">No contacts found</Text>
              )
            ) : (
              <>
                {results.items.map((c) => (
                  <Box
                    key={c.id}
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: 'accent.soft' }}
                    onClick={() => handleSelect(c.id, c.fullName)}
                  >
                    <Text fontSize="sm" fontWeight="medium">{c.fullName}</Text>
                    {(c.phone || c.email) && (
                      <Text fontSize="xs" color="gray.500" lineClamp={1}>
                        {[c.phone, c.email].filter(Boolean).join(' · ')}
                      </Text>
                    )}
                  </Box>
                ))}
                {allowFreeText && (
                  <Box
                    px={3} py={2} cursor="pointer" borderTop="1px solid" borderColor="gray.100"
                    _hover={{ bg: 'gray.50' }}
                    onClick={() => { onSelect(null, search.trim()); setSearch(''); setIsOpen(false) }}
                  >
                    <Text fontSize="xs" color="gray.500">Use "{search.trim()}" as name (no match)</Text>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Portal>
      )}
    </Box>
  );
}
